import {
  DayOfWeek,
  Duration,
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  Month,
  MonthDay,
  Period,
  Year,
  YearMonth,
} from "@js-joda/core";
import { deepEqual } from "../deep-equal";
import { HashMap } from "./HashMap";
import { KeyedList, List } from "./List";

/**
 * Works on the following types:
 *
 * - Primitives:
 *   - string
 *   - number
 *   - boolean
 *   - null
 *   - undefined
 * - Data Structures:
 *   - HashMap (utils/Hashmap.ts)
 *   - KeyedList (utils/List.ts)
 * - datetime (js-joda):
 *   - DayOfWeek
 *   - Duration
 *   - Instant
 *   - LocalDate
 *   - LocalDateTime
 *   - LocalTime
 *   - Month
 *   - MonthDay
 *   - Period
 *   - Year
 *   - YearMonth
 * - JavaScript Object whose field values are any of the above
 */
export function genericUpdate<T extends object>(old: T | null | undefined, updated: T): T {
  if (typeof old === "string") {
    return updated;
  }
  if (typeof old === "number") {
    return updated;
  }
  if (typeof old === "boolean") {
    return updated;
  }
  if (old === null) {
    return updated;
  }
  if (old === undefined) {
    return updated;
  }

  if (
    old instanceof DayOfWeek ||
    old instanceof Duration ||
    old instanceof Instant ||
    old instanceof LocalDate ||
    old instanceof LocalDateTime ||
    old instanceof LocalTime ||
    old instanceof MonthDay ||
    old instanceof Period ||
    old instanceof YearMonth
  ) {
    if (old.equals(updated as any)) {
      return old;
    } else {
      return updated;
    }
  }

  if (old instanceof Month) {
    if (updated instanceof Month && old.value() === updated.value()) {
      return old;
    } else {
      return updated;
    }
  }
  if (old instanceof Year) {
    if (updated instanceof Year && old.value() === updated.value()) {
      return old;
    } else {
      return updated;
    }
  }

  if (old instanceof KeyedList) {
    if (updated instanceof KeyedList && old.keyField === updated.keyField) {
      return genericUpdateKeyedList(old, updated) as any;
    } else {
      return updated;
    }
  }

  if (old instanceof HashMap) {
    if (updated instanceof HashMap) {
      return genericUpdateHashMap(old, updated) as any;
    } else {
      return updated;
    }
  }

  if (Array.isArray(old)) {
    // TODO Might be better to have a "config" param to genericUpdate that
    // specifies if arrays are allowed (or even better, which field names
    // are allowed to be arrays)

    // throw new Error("Invalid call to genericUpdate: Array found");

    if (deepEqual(old, updated)) {
      return old;
    } else {
      return updated;
    }
  }
  if (old instanceof Map || updated instanceof Map) {
    throw new Error("Invalid call to genericUpdate: Map found");
  }
  if (old instanceof Set || updated instanceof Set) {
    throw new Error("Invalid call to genericUpdate: Set found");
  }
  if (old instanceof List || updated instanceof List) {
    if (old === updated) {
      return old;
    } else {
      return updated;
    }
  }

  const newObj: any = {};

  let changed: boolean = false;
  if (typeof old === "object") {
    if (
      (updated as any) === null ||
      (updated as any) === undefined ||
      typeof updated !== "object"
    ) {
      return updated;
    } else {
      for (const key of Object.keys(updated)) {
        const oldVal = (old as any)[key];
        const updatedVal = (updated as any)[key];

        const newVal = genericUpdate(oldVal, updatedVal);
        if (newVal !== oldVal || !{}.hasOwnProperty.call(old, key)) {
          changed = true;
        }
        newObj[key] = newVal;
      }
    }
  }

  if (changed || Object.keys(updated).length < Object.keys(old).length) {
    return newObj;
  } else {
    return old;
  }
}

function lookupKeyedList<A, F extends keyof A>(
  list: KeyedList<A, F>,
  key: unknown
): [A, number] | null {
  let index = 0;
  for (const elem of list) {
    if (deepEqual(elem[list.keyField], key)) {
      return [elem, index];
    }
    ++index;
  }

  return null;
}

function genericUpdateKeyedList<A extends object, F extends keyof A>(
  old: KeyedList<A, F>,
  updated: KeyedList<A, F>
): KeyedList<A, F> {
  // TODO Optimization: If old.length() > THRESHOLD then switch to a more efficient log(n) algorithm

  if (old.length() === updated.length()) {
    // If the lengths of the lists are the same, then it is possible that
    // the updated list is equal to the old list, and in that case we will
    // end up just throwing away our resultArray. In that case, it will
    // have been more efficient to work with it as an array.

    const resultArray: A[] = [];

    let changed: boolean = false;
    let index = 0;
    for (const elem of updated) {
      const matchingElemResult = lookupKeyedList(old, elem[old.keyField]);
      if (matchingElemResult !== null) {
        const [matchingElem, matchingIndex] = matchingElemResult;
        const newElem = genericUpdate(matchingElem, elem);
        if (newElem !== matchingElem || index !== matchingIndex) {
          changed = true;
        }
        resultArray.push(newElem);
      } else {
        changed = true;
        resultArray.push(elem);
      }
      ++index;
    }

    if (changed) {
      return KeyedList.from<A>(resultArray)(old.keyField);
    } else {
      return old;
    }
  } else {
    // If the lengths of the lists are different, then we know for certain
    // that we will need to build up a new KeyedList, so we build it
    // directly.

    let resultList: KeyedList<A, F> = KeyedList.empty<A>()(old.keyField);

    for (const elem of updated) {
      const matchingElemResult = lookupKeyedList(old, elem[old.keyField]);
      if (matchingElemResult !== null) {
        const [matchingElem] = matchingElemResult;
        const newElem = genericUpdate(matchingElem, elem);
        resultList = resultList.append(newElem);
      } else {
        resultList = resultList.append(elem);
      }
    }

    return resultList;
  }
}

function genericUpdateHashMap<K, V extends object>(
  old: HashMap<K, V>,
  updated: HashMap<K, V>
): HashMap<K, V> {
  let resultMap: HashMap<K, V> = HashMap.empty<K, V>();

  let changed: boolean = false;
  for (const [key, value] of updated.entries()) {
    const matchingElem = old.get(key);
    if (matchingElem !== undefined) {
      const newValue = genericUpdate(matchingElem, value);
      if (newValue !== matchingElem) {
        changed = true;
      }
      resultMap = resultMap.set(key, newValue);
    } else {
      changed = true;
      resultMap = resultMap.set(key, value);
    }
  }

  if (changed || old.size() !== updated.size()) {
    return resultMap;
  } else {
    return old;
  }
}

export class Tests {
  static test_empty() {
    const a = {};

    const b: typeof a = {};

    const c = genericUpdate<{}>(a, b);
    Tests.assertEqual(a, c);
  }

  static test_objChangeAndRemoveKey() {
    const a = {
      foo: {},
      bar: 5,
    };

    const b = {
      bar: 6,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_objRemoveKey() {
    const a = {
      foo: {},
      bar: 5,
    };

    const b = {
      bar: 5,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_objNullKey() {
    const a = {
      foo: {},
    };

    const b = {
      foo: null,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_objAddUndefinedValue() {
    const a = {
      foo: {},
    };

    const b = {
      foo: {},
      bar: undefined,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_jodaDurationToNull() {
    const a = {
      foo: Duration.ofMinutes(5),
    };

    const b = {
      foo: null,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_jodaMonthToNull() {
    const a = {
      foo: Month.FEBRUARY,
    };

    const b = {
      foo: null,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_jodaYearToNull() {
    const a = {
      foo: Year.of(2019),
    };

    const b = {
      foo: null,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_KeyedListToNull() {
    const a = {
      foo: KeyedList.empty<{ id: number }>()("id"),
    };

    const b = {
      foo: null,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_HashMapToNull() {
    const a = {
      foo: HashMap.empty<number, {}>(),
    };

    const b = {
      foo: null,
    };

    const c = genericUpdate<{}>(a, b);
    Tests.assertDeepEqual(c, b);
  }

  static test_KeyedListSame() {
    const a = KeyedList.from<{ id: number }>([{ id: 1 }, { id: 2 }])("id");
    const b = KeyedList.from<{ id: number }>([{ id: 1 }, { id: 2 }])("id");

    const c = genericUpdate(a, b);

    Tests.assertEqual(a, c);
  }

  static test_KeyedListAppend() {
    const a = KeyedList.from<{ id: number }>([{ id: 1 }])("id");
    const b = KeyedList.from<{ id: number }>([{ id: 1 }, { id: 2 }])("id");

    const c = genericUpdate(a, b);

    Tests.assertEqual(a.nth(0), c.nth(0));
    Tests.assertDeepEqual(c.nth(1), { id: 2 });
    Tests.assertEqual(c.length(), 2);
  }

  static test_KeyedListSwap() {
    const a = KeyedList.from<{ id: number }>([{ id: 2 }, { id: 1 }])("id");
    const b = KeyedList.from<{ id: number }>([{ id: 1 }, { id: 2 }])("id");

    const c = genericUpdate(a, b);

    Tests.assertDeepEqual(b.toArray(), c.toArray());
    Tests.assertEqual(c.nth(0), a.nth(1));
    Tests.assertEqual(c.nth(1), a.nth(0));
    Tests.assertEqual(c.length(), 2);
  }

  static test_KeyedListModify() {
    const a = KeyedList.from<{ id: number; foo: string }>([
      { id: 1, foo: "a" },
      { id: 2, foo: "b" },
    ])("id");
    const b = KeyedList.from<{ id: number; foo: string }>([
      { id: 1, foo: "a" },
      { id: 2, foo: "c" },
    ])("id");

    const c = genericUpdate(a, b);

    Tests.assertEqual(c.nth(0), a.nth(0));
    Tests.assertDeepEqual(c.nth(1), { id: 2, foo: "c" });
    Tests.assertEqual(c.length(), 2);
  }

  static test_KeyedListRemove() {
    const a = KeyedList.from<{ id: number }>([{ id: 2 }, { id: 1 }])("id");
    const b = KeyedList.from<{ id: number }>([{ id: 1 }])("id");

    const c = genericUpdate(a, b);

    Tests.assertEqual(c.nth(0), a.nth(1));
    Tests.assertEqual(c.length(), 1);
  }

  static test_HashMapSame() {
    const a = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);
    const b = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);

    const c = genericUpdate(a, b);

    Tests.assertEqual(a, c);
  }

  static test_HashMapAdd() {
    const a = HashMap.fromArray<number, { foo: string }>([[1, { foo: "a" }]]);
    const b = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);

    const c = genericUpdate(a, b);

    Tests.assertEqual(c.get(1), a.get(1));
    Tests.assertDeepEqual(c.get(2), { foo: "b" });
    Tests.assertEqual(c.size(), 2);
  }

  static test_HashMapModify() {
    const a = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);
    const b = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "c" }],
    ]);

    const c = genericUpdate(a, b);

    Tests.assertEqual(c.get(1), a.get(1));
    Tests.assertDeepEqual(c.get(2), { foo: "c" });
    Tests.assertEqual(c.size(), 2);
  }

  static test_HashMapRemove() {
    const a = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);
    const b = HashMap.fromArray<number, { foo: string }>([[2, { foo: "b" }]]);

    const c = genericUpdate(a, b);

    Tests.assertEqual(c.get(2), a.get(2));
    Tests.assertEqual(c.size(), 1);
  }

  static test_HashAddMapRemove() {
    const a = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);
    const b = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [3, { foo: "c" }],
    ]);

    const c = genericUpdate(a, b);

    Tests.assertEqual(c.get(1), a.get(1));
    Tests.assertDeepEqual(c.get(3), { foo: "c" });
    Tests.assertEqual(c.size(), 2);
  }

  static test_HashMapRemoveModify() {
    const a = HashMap.fromArray<number, { foo: string }>([
      [1, { foo: "a" }],
      [2, { foo: "b" }],
    ]);
    const b = HashMap.fromArray<number, { foo: string }>([[1, { foo: "c" }]]);

    const c = genericUpdate(a, b);

    Tests.assertDeepEqual(c.get(1), { foo: "c" });
    Tests.assertEqual(c.size(), 1);
  }

  static testAll() {
    for (const key of Object.keys(Tests)) {
      if (key.lastIndexOf("test_", 0) === 0) {
        console.log(key);
        (Tests as any)[key]();
      }
    }
  }

  static assertEqual<T>(a: T, b: T) {
    if (a !== b) {
      throw new Error(`${JSON.stringify(a)} !== ${JSON.stringify(b)}`);
    }
  }

  static assertDeepEqual<T>(a: T, b: T) {
    if (!deepEqual(a, b)) {
      throw new Error(`!deepEqual(${JSON.stringify(a)}, ${JSON.stringify(b)})`);
    }
  }
}

if (typeof require !== "undefined" && require.main === module) {
  // require("source-map-support/register");

  Tests.testAll();
} else {
  // (<any>window).Tests = Tests;
}
